import React from 'react';
import { Select } from 'antd';

import ToggleRow from '../common/ToggleRow';
import { optionSearchFilter } from '../Input';
import BuilderStep from './BuilderStep';
import { Field } from './controls';
import FieldMessage, { messageProps } from './FieldMessage';

/** The card select's value for a chart of every card (ruled d20: `card_id: null`). */
export const EVERY_CARD = '__every';

/** The Count select's value for counting items (a group's entries are counted by the group's name). */
export const COUNT_ITEMS = '__items';

/**
 * Step 1, Items: whose items the chart counts — one card (its child cards
 * included, as the list counts a card) or **Every card**, and, in a
 * collection with sub-collections, whether theirs count too. On a card that
 * holds a repeating group, **Count** chooses the items or the group's entries
 * (KPR-684: "ingredient counts by name" counts entries, "recipes that use
 * butter" counts items). Counting entries, the filter's block on the group
 * picks the entries too (KPR-936), and the hint says so.
 *
 *   cardOptions  [{ value, label (the card's badge), searchText, disabled?, reason? }]
 */
export default function ItemsStep({ draft, view, cardOptions, onCard, onEntries, onSubCollections, messagesFor, step }) {
	const cardMessages = messagesFor('card');
	const countMessages = messagesFor('entries');
	const subMessages = messagesFor('subCollections');
	const counting = view.counting;
	// The counted group by its label, as the filter builder words a block on it (KPR-936).
	const countedOption = counting && draft.entries ? counting.options.find((o) => o.value === draft.entries) : null;
	const countedGroup = (countedOption && countedOption.group) || draft.entries;
	const options = [
		{ value: EVERY_CARD, label: 'Every card', display: 'Every card', searchText: 'Every card' },
		...cardOptions.map((o) => ({
			value: o.value,
			display: o.label,
			disabled: Boolean(o.disabled),
			searchText: o.searchText,
			title: o.reason || o.searchText,
			label: o.reason ? (
				<span className='chart-builder__option'>
					<span className='chart-builder__option-label'>{o.label}</span>
					<span className='chart-builder__option-why'>{o.reason}</span>
				</span>
			) : o.label,
		})),
	];
	return (
		<BuilderStep step='items' number={1} title='Items' {...step}>
			<Field
				id='chart-builder-card'
				label='Card'
				messages={cardMessages}
				hint={draft.cardId ? 'Its child cards count too, as the list counts them.' : 'Every card counts; a chart of every card groups by its dates, its cards and its collections.'}
			>
				<Select
					id='chart-builder-card'
					className='chart-builder__select'
					value={draft.cardId || EVERY_CARD}
					options={options}
					onChange={(value) => onCard(value === EVERY_CARD ? null : value)}
					showSearch
					filterOption={optionSearchFilter}
					optionLabelProp='display'
					popupMatchSelectWidth={false}
					{...messageProps(cardMessages, 'chart-builder-card-messages')}
				/>
			</Field>
			{counting ? (
				<Field
					id='chart-builder-entries'
					label='Count'
					messages={countMessages}
					hint={draft.entries
						? `Each entry counts once: an item with three entries counts three. When the filter says ${countedGroup} has an entry where…, only the entries that match count. Measure its columns; group by them or by the item's own elements.`
						: 'Each item counts once. Group by a column to count the items that hold each value.'}
				>
					<Select
						id='chart-builder-entries'
						className='chart-builder__select'
						value={draft.entries || COUNT_ITEMS}
						options={counting.options.map((o) => ({ value: o.value || COUNT_ITEMS, label: o.label }))}
						onChange={(value) => onEntries(value === COUNT_ITEMS ? null : value)}
						popupMatchSelectWidth={false}
						{...messageProps(countMessages, 'chart-builder-entries-messages')}
					/>
				</Field>
			) : null}
			{view.subCollections ? (
				<>
					<ToggleRow
						id='chart-builder-sub-collections'
						label='Include sub-collections'
						description="Count their items too, as this collection's list does."
						checked={draft.subCollections}
						onChange={onSubCollections}
					/>
					<FieldMessage messages={subMessages} id='chart-builder-sub-collections-messages' />
				</>
			) : null}
		</BuilderStep>
	);
}
